'use client'

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import dynamic from 'next/dynamic'
import { cn, colSpanMap } from '../../../lib/utils'

import { type InputProps } from '../Input'
import { type SecretInputProps } from '../SecretInput'
import { type ButtonProps } from '../Button'
import { type ImageSettingProps } from '../ImageSetting'
import { type FileSettingProps } from '../FileSetting'
import { type DataTableProps } from '../DataTable'
import { type SelectProps } from '../Select'
import { type MultiSelectProps } from '../MultiSelect'
import { type ViewPortProps } from '../ViewPort'
import { type FieldSettingProps } from '../FieldSetting'
import { type TextareaProps } from '..'
import { type CheckboxProps } from '../Checkbox'
import { type ProfileProps } from '../Profile'
import { type AutoCompleteProps } from '../AutoComplete'
import { type LineProps } from '../Line'
import { type AlertProps } from '../Alert'
import { type CodeConfigProps } from '../CodeConfig'
import { type SliderProps, Slider } from '../Slider'
import { type DescriptiveToggleProps } from '../DescriptiveToggle'
import { type ActionTreeProps } from '../SideNavigation/components/ActionTree'
import { type ComboBoxProps } from '../Combobox'
import { type ListProps } from '../List'
import { type DropdownMenuProps } from '../DropDownMenu'
import { type TimePickerProps } from '../TimePicker'
import { type ChatWindowProps } from '../ShadcnChat/components/ChatWindow'
import { type FieldChangeProps } from '../FieldChange'
import { type DatePickerProps } from '../DatePicker'
import { type IconSelectorProps } from '../Icon'
import type { AppIntentProps } from '../AppIntent'
import { type EditableListProps, type EditableListItem } from '../EditableList'
import { type SchemaBuilderProps } from '../SchemaBuilder'
import { type SwitchProps } from '../Switch'
import {
  type ToolBindingBuilderProps,
  type ToolBinding,
} from '../ToolBindingBuilder'
import { type SegmentedIconSelectorProps } from '../SegmentedIconSelector'
import { type LiquidInputProps } from '../LiquidInput'
import { type EventInputMappingEditorProps } from '../EventInputMappingEditor'
import { type TriggerSentenceListProps } from '../TriggerSentence'
const AppIntent = dynamic(
  () => import('../AppIntent').then((mod) => mod.AppIntent),
  { ssr: false },
)
const SegmentedIconSelector = dynamic(
  () =>
    import('../SegmentedIconSelector').then((mod) => mod.SegmentedIconSelector),
  { ssr: false },
)
const LiquidInput = dynamic(
  () => import('../LiquidInput').then((mod) => mod.LiquidInput),
  { ssr: false },
)
const EventInputMappingEditor = dynamic(
  () =>
    import('../EventInputMappingEditor').then(
      (mod) => mod.EventInputMappingEditor,
    ),
  { ssr: false },
)
const TriggerSentenceList = dynamic(
  () =>
    import('../TriggerSentence').then((mod) => mod.TriggerSentenceList),
  { ssr: false },
)

const Input = dynamic(() => import('../Input').then((mod) => mod.Input), {
  ssr: false,
})
const SecretInput = dynamic(
  () => import('../SecretInput').then((mod) => mod.SecretInput),
  { ssr: false },
)
const Button = dynamic(() => import('../Button').then((mod) => mod.Button), {
  ssr: false,
})

const IconSelector = dynamic(
  () => import('../Icon/IconSelector').then((mod) => mod.IconSelector),
  { ssr: false },
)

const ImageSetting = dynamic(
  () => import('../ImageSetting').then((mod) => mod.ImageSetting),
  { ssr: false },
)
const FileSetting = dynamic(
  () => import('../FileSetting').then((mod) => mod.FileSetting),
  { ssr: false },
)
const DataTable = dynamic(
  () => import('../DataTable').then((mod) => mod.DataTable),
  { ssr: false },
)
const Select = dynamic(() => import('../Select').then((mod) => mod.Select), {
  ssr: false,
})
const MultiSelect = dynamic(
  () => import('../MultiSelect').then((mod) => mod.MultiSelect),
  { ssr: false },
)
const ViewPort = dynamic(
  () => import('../ViewPort').then((mod) => mod.ViewPort),
  { ssr: false },
)
const FieldSetting = dynamic(
  () => import('../FieldSetting').then((mod) => mod.FieldSetting),
  { ssr: false },
)
const Textarea = dynamic(
  () => import('../Textarea').then((mod) => mod.Textarea),
  { ssr: false },
)
const Checkbox = dynamic(
  () => import('../Checkbox').then((mod) => mod.Checkbox),
  { ssr: false },
)
const Switch = dynamic(() => import('../Switch').then((mod) => mod.Switch), {
  ssr: false,
})
const Profile = dynamic(() => import('../Profile').then((mod) => mod.Profile), {
  ssr: false,
})
const AutoComplete = dynamic(
  () => import('../AutoComplete').then((mod) => mod.AutoComplete),
  { ssr: false },
)
const Lines = dynamic(() => import('../Line').then((mod) => mod.Lines), {
  ssr: false,
})
const AlertWrapper = dynamic(
  () => import('../Alert').then((mod) => mod.AlertWrapper),
  { ssr: false },
)
const DescriptiveToggle = dynamic(
  () => import('../DescriptiveToggle').then((mod) => mod.DescriptiveToggle),
  { ssr: false },
)
const ActionTree = dynamic(
  () =>
    import('../SideNavigation/components/ActionTree').then(
      (mod) => mod.ActionTree,
    ),
  { ssr: false },
)
const Combobox = dynamic(
  () => import('../Combobox').then((mod) => mod.Combobox),
  { ssr: false },
)
const List = dynamic(() => import('../List').then((mod) => mod.List), {
  ssr: false,
})
const DropdownMenu = dynamic(
  () => import('../DropDownMenu').then((mod) => mod.DropdownMenu),
  { ssr: false },
)
const TimePicker = dynamic(
  () => import('../TimePicker').then((mod) => mod.TimePicker),
  { ssr: false },
)

const CodeConfig = dynamic(
  () => import('../CodeConfig').then((mod) => mod.CodeConfig),
  { ssr: false },
)

const ChatWindow = dynamic(
  () =>
    import('../ShadcnChat/components/ChatWindow').then((mod) => mod.ChatWindow),
  { ssr: false },
)

const FieldChange = dynamic(
  () => import('../FieldChange').then((mod) => mod.FieldChange),
  { ssr: false },
)

const DatePicker = dynamic(
  () => import('../DatePicker').then((mod) => mod.DatePicker),
  { ssr: false },
)

const EditableList = dynamic(
  () => import('../EditableList').then((mod) => mod.EditableList),
  { ssr: false },
)

const SchemaBuilder = dynamic(
  () => import('../SchemaBuilder').then((mod) => mod.SchemaBuilder),
  { ssr: false },
)

const ToolBindingBuilder = dynamic(
  () => import('../ToolBindingBuilder').then((mod) => mod.ToolBindingBuilder),
  { ssr: false },
)

const pageContentVariants = cva('h-full w-full')

type SpanCol = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12

export type StyleProps = {
  id?: string
  className?: string
  span: SpanCol
  mdSpan: SpanCol
  lgSpan: SpanCol
  hidden?: boolean
}

export type IconSelectorComponent = {
  component: 'IconSelector'
  props: IconSelectorProps
} & StyleProps

export type TextComponent = {
  component: 'Text'
  props: {}
} & StyleProps

export type EmptyComponent = {
  component: 'Empty'
  props: {}
} & StyleProps

export type DataTableComponent = {
  component: 'DataTable'
  props: DataTableProps
} & StyleProps

export type InputComponent = {
  component: 'Input'
  props: InputProps
} & StyleProps

export type SecretInputComponent = {
  component: 'SecretInput'
  props: SecretInputProps
} & StyleProps

export type TextareaComponent = {
  component: 'Textarea'
  props: TextareaProps
} & StyleProps

export type ButtonComponent = {
  component: 'Button'
  props: ButtonProps
} & StyleProps

export type ImageSettingComponent = {
  component: 'ImageSetting'
  props: ImageSettingProps
} & StyleProps

export type FileSettingComponent = {
  component: 'FileSetting'
  props: FileSettingProps
} & StyleProps

export type FieldSettingComponent = {
  component: 'FieldSetting'
  props: FieldSettingProps
} & StyleProps

export type SelectComponent = {
  component: 'Select'
  props: SelectProps
} & StyleProps

export type MultiSelectComponent = {
  component: 'MultiSelect'
  props: MultiSelectProps
} & StyleProps

export type ViewPortFormComponent = {
  component: 'ViewPort'
  props: ViewPortProps
} & StyleProps

export type CheckboxComponent = {
  component: 'Checkbox'
  props: CheckboxProps
} & StyleProps

export type ProfileComponent = {
  component: 'Profile'
  props: ProfileProps
} & StyleProps

export type AutoCompleteComponent = {
  component: 'AutoComplete'
  props: AutoCompleteProps
} & StyleProps

export type LinesComponent = {
  component: 'Lines'
  props: LineProps
} & StyleProps

export type AlertComponent = {
  component: 'Alert'
  props: AlertProps
} & StyleProps

export type DescriptiveToggleComponent = {
  component: 'DescriptiveToggle'
  props: DescriptiveToggleProps
} & StyleProps

export type ActionTreeComponent = {
  component: 'ActionTree'
  props: ActionTreeProps
} & StyleProps

export type ComboBoxComponent = {
  component: 'ComboBox'
  props: ComboBoxProps
} & StyleProps

export type ListComponent = {
  component: 'List'
  props: ListProps
} & StyleProps

export type AppIntentComponent = {
  component: 'AppIntent'
  props: AppIntentProps
} & StyleProps

export type SliderComponent = {
  component: 'Slider'
  props: SliderProps
} & StyleProps

export type CodeConfigComponent = {
  component: 'CodeConfig'
  props: CodeConfigProps
} & StyleProps

export type DropDownMenuComponent = {
  component: 'DropDown'
  props: DropdownMenuProps
} & StyleProps

export type TimePickerComponent = {
  component: 'TimePicker'
  props: TimePickerProps
} & StyleProps

export type ChatWindowComponent = {
  component: 'ChatWindow'
  props: ChatWindowProps
} & StyleProps

export type FieldChangeComponent = {
  component: 'FieldChange'
  props: FieldChangeProps
} & StyleProps

export type DatePickerComponent = {
  component: 'DatePicker'
  props: DatePickerProps
} & StyleProps

export type EditableListComponent = {
  component: 'EditableList'
  props: EditableListProps<EditableListItem>
} & StyleProps

export type SchemaBuilderComponent = {
  component: 'SchemaBuilder'
  props: SchemaBuilderProps
} & StyleProps

export type ToolBindingBuilderComponent = {
  component: 'ToolBindingBuilder'
  props: ToolBindingBuilderProps
} & StyleProps

export type SwitchComponent = {
  component: 'Switch'
  props: SwitchProps & {
    id?: string
    label?: string
    helpText?: string
    onCheckedChange?: (checked: boolean) => void
    checked?: boolean
  }
} & StyleProps

export type SegmentedIconSelectorComponent = {
  component: 'SegmentedIconSelector'
  props: SegmentedIconSelectorProps
} & StyleProps

export type LiquidInputComponent = {
  component: 'LiquidInput'
  props: LiquidInputProps
} & StyleProps

export type EventInputMappingEditorComponent = {
  component: 'EventInputMappingEditor'
  props: EventInputMappingEditorProps
} & StyleProps

export type TriggerSentenceListComponent = {
  component: 'TriggerSentenceList'
  props: TriggerSentenceListProps
} & StyleProps

export type FormComponent =
  | InputComponent
  | SecretInputComponent
  | ButtonComponent
  | ImageSettingComponent
  | FileSettingComponent
  | DataTableComponent
  | SelectComponent
  | MultiSelectComponent
  | ViewPortFormComponent
  | FieldSettingComponent
  | TextareaComponent
  | CheckboxComponent
  | SwitchComponent
  | ProfileComponent
  | AutoCompleteComponent
  | LinesComponent
  | EmptyComponent
  | AlertComponent
  | TextComponent
  | DescriptiveToggleComponent
  | ActionTreeComponent
  | ListComponent
  | ComboBoxComponent
  | SliderComponent
  | CodeConfigComponent
  | DropDownMenuComponent
  | TimePickerComponent
  | ChatWindowComponent
  | FieldChangeComponent
  | DatePickerComponent
  | IconSelectorComponent
  | AppIntentComponent
  | EditableListComponent
  | SchemaBuilderComponent
  | ToolBindingBuilderComponent
  | SegmentedIconSelectorComponent
  | LiquidInputComponent
  | EventInputMappingEditorComponent
  | TriggerSentenceListComponent

export interface FormLayoutProps
  extends VariantProps<typeof pageContentVariants> {
  id?: string
  fields: FormComponent[]
  className?: string
  /** Force single-column layout (ignore md/lg breakpoints). */
  stackFields?: boolean
}

const FormLayout = React.forwardRef<HTMLDivElement, FormLayoutProps>(
  ({ id = 'form-set', fields, className = '', stackFields = false }, ref) => {
    return (
      <div
        ref={ref}
        id={id}
        className={cn('grid grid-cols-12 gap-4', className)}
      >
        {fields
          .filter((comp) => !!!comp.hidden)
          .map((field, i) => (
            <div
              key={`${id}-${field.component}-${i}`}
              className={cn(
                stackFields
                  ? 'col-span-12'
                  : [
                      `col-span-${field.span}`,
                      `md:col-span-${field.mdSpan}`,
                      `lg:col-span-${field.lgSpan}`,
                    ],
                'w-full relative',
                field.className,
              )}
            >
              {renderField(field)}
            </div>
          ))}
      </div>
    )
  },
)

// Helper function to render a field based on its component type
const renderField = (field: FormComponent) => {
  switch (field.component) {
    case 'Input':
      return <Input {...field.props} />
    case 'SecretInput':
      return <SecretInput {...field.props} />
    case 'Select':
      return <Select {...field.props} />
    case 'MultiSelect':
      return <MultiSelect {...field.props} />
    case 'Button':
      return <Button {...field.props} />
    case 'ImageSetting':
      return <ImageSetting {...field.props} />
    case 'FileSetting':
      return <FileSetting {...field.props} />
    case 'DataTable':
      return (
        <div className="-mx-4">
          <DataTable {...field.props} />
        </div>
      )
    case 'ViewPort':
      return <ViewPort {...field.props} />
    case 'FieldSetting':
      return <FieldSetting {...field.props} />
    case 'Textarea':
      return <Textarea {...field.props} />
    case 'Checkbox':
      return <Checkbox {...field.props} />
    case 'Switch':
      return <Switch {...field.props} />
    case 'Profile':
      return <Profile {...field.props} />
    case 'AutoComplete':
      return <AutoComplete {...field.props} />
    case 'Lines':
      return <Lines {...field.props} />
    case 'Alert':
      return <AlertWrapper {...field.props} />
    case 'Text':
      return <div {...field.props}></div>
    case 'Empty':
      return <div></div>
    case 'DescriptiveToggle':
      return <DescriptiveToggle {...field.props} />
    case 'ComboBox':
      return <Combobox {...field.props} />
    case 'ActionTree':
      return <ActionTree {...field.props} />
    case 'List':
      return <List {...field.props} />
    case 'Slider':
      return <Slider {...field.props} />
    case 'CodeConfig':
      return <CodeConfig {...field.props} />
    case 'DropDown':
      return <DropdownMenu {...field.props} />
    case 'TimePicker':
      return <TimePicker {...field.props} />
    case 'ChatWindow':
      return <ChatWindow {...field.props} />
    case 'FieldChange':
      return <FieldChange {...field.props} />
    case 'DatePicker':
      return <DatePicker {...field.props} />
    case 'IconSelector':
      return <IconSelector {...field.props} />
    case 'AppIntent':
      return <AppIntent {...field.props} />
    case 'EditableList':
      return <EditableList {...field.props} />
    case 'SchemaBuilder':
      return <SchemaBuilder {...field.props} />
    case 'ToolBindingBuilder':
      return <ToolBindingBuilder {...field.props} />
    case 'SegmentedIconSelector':
      return <SegmentedIconSelector {...field.props} />
    case 'LiquidInput':
      return <LiquidInput {...field.props} />
    case 'EventInputMappingEditor':
      return <EventInputMappingEditor {...field.props} />
    case 'TriggerSentenceList':
      return <TriggerSentenceList {...field.props} />
    default:
      return null
  }
}

export { FormLayout }

FormLayout.displayName = 'FormLayout'
